/**
 * presets.ts — Built-in layout presets for Chanelle OS.
 *
 * Each preset defines the default widget layout for every tab.
 * The user can apply a preset from Settings, which replaces their
 * current layout with the preset layout (after confirmation).
 *
 * Presets: Default | Power User | Minimal | Focused
 */

import type { TabLayout, PresetName, WidgetGridItem } from '../core/types';
import { APP_TABS } from '../core/utils/defaults';

// -------------------------------------------------------
// Helper to build a WidgetGridItem with sensible defaults
// -------------------------------------------------------

function w(
  widgetId: string,
  x: number,
  y: number,
  cols: number,
  rows: number,
  config?: Record<string, unknown>
): WidgetGridItem {
  return {
    instanceId: `${widgetId}_preset_${x}${y}`,
    widgetId,
    x,
    y,
    w: cols,
    h: rows,
    config: config ?? {},
  };
}

// -------------------------------------------------------
// Preset: Default
// A balanced, information-dense layout suitable for most users.
// -------------------------------------------------------

const defaultPreset: Record<string, TabLayout> = {
  // Zone 1: Home HQ — Focus suggestion, tasks, alerts, quick notes, calendar
  home: {
    tabId: 'home',
    widgets: [
      w('focus_suggestion', 0,  0,  8,  3),
      w('alerts',           8,  0,  4,  3),
      w('my_tasks',         0,  3,  6,  4),
      w('inbox',            6,  3,  6,  4),
      w('vault_capture',    0,  7,  4,  3),
      w('quick_actions',    4,  7,  4,  3),
      w('google_calendar',  8,  7,  4,  3),
    ],
  },
  // Zone 2: My Work — Full task list with due-today filter, board health, mentions
  my_work: {
    tabId: 'my_work',
    widgets: [
      w('my_tasks',       0,  0,  8,  6),
      w('my_tasks',       8,  0,  4,  3, { filter: 'due_today', maxItems: 5 }),
      w('board_health',   8,  3,  4,  3),
      w('inbox',          0,  6,  6,  3, { filterType: 'mentions', maxItems: 10 }),
      w('alerts',         6,  6,  6,  3),
    ],
  },
  // Zone 3: Inbox — Full inbox + alerts sidebar
  inbox: {
    tabId: 'inbox',
    widgets: [
      w('inbox',          0,  0,  8,  8),
      w('alerts',         8,  0,  4,  4),
      w('inbox',          8,  4,  4,  4, { filterType: 'mentions', maxItems: 10 }),
    ],
  },
  // Zone 4: Vault — Capture, notes, topics, meetings
  vault: {
    tabId: 'vault',
    widgets: [
      w('vault_capture',  0,  0,  5,  4),
      w('recent_notes',   5,  0,  7,  4),
      w('topics',         0,  4,  6,  3),
      w('meetings',       6,  4,  6,  3),
    ],
  },
  // Zone 5: Boards Monitor — Pulse, health, alerts
  monitor: {
    tabId: 'monitor',
    widgets: [
      w('board_pulse',    0,  0,  6,  5),
      w('board_health',   6,  0,  6,  5),
      w('alerts',         0,  5,  12, 3),
    ],
  },
  // Calendar — Google Calendar + tasks sidebar
  calendar: {
    tabId: 'calendar',
    widgets: [
      w('google_calendar',  0,  0,  8,  6),
      w('my_tasks',       8,  0,  4,  6, { filter: 'due_today', maxItems: 10 }),
    ],
  },
  // Zone 6: Analytics
  analytics: {
    tabId: 'analytics',
    widgets: [
      w('analytics_snapshot', 0, 0, 12, 6),
    ],
  },
  // Zone 7: System (Settings)
  settings: {
    tabId: 'settings',
    widgets: [],
  },
};

// -------------------------------------------------------
// Preset: Power User
// Maximises information density. More widgets per tab.
// -------------------------------------------------------

const powerUserPreset: Record<string, TabLayout> = {
  home: {
    tabId: 'home',
    widgets: [
      w('focus_suggestion',   0,  0,  6,  3),
      w('alerts',             6,  0,  3,  3),
      w('analytics_snapshot', 9,  0,  3,  3),
      w('my_tasks',           0,  3,  4,  5),
      w('inbox',              4,  3,  4,  5),
      w('board_pulse',        8,  3,  4,  4),
      w('vault_capture',      0,  8,  4,  3),
      w('recent_notes',       4,  8,  4,  3),
      w('google_calendar',    8,  7,  4,  3),
    ],
  },
  my_work: {
    tabId: 'my_work',
    widgets: [
      w('my_tasks',       0,  0,  7,  6),
      w('my_tasks',       7,  0,  5,  3, { filter: 'due_today', maxItems: 5 }),
      w('board_health',   7,  3,  5,  3),
      w('inbox',          0,  6,  4,  3, { filterType: 'mentions', maxItems: 8 }),
      w('alerts',         4,  6,  4,  3),
      w('google_calendar',8,  6,  4,  3),
    ],
  },
  inbox: {
    tabId: 'inbox',
    widgets: [
      w('inbox',          0,  0,  8,  8),
      w('alerts',         8,  0,  4,  4),
      w('inbox',          8,  4,  4,  4, { filterType: 'mentions', maxItems: 10 }),
    ],
  },
  vault: {
    tabId: 'vault',
    widgets: [
      w('vault_capture',  0,  0,  4,  4),
      w('recent_notes',   4,  0,  5,  4),
      w('topics',         9,  0,  3,  4),
      w('meetings',       0,  4,  6,  3),
      w('quick_actions',  6,  4,  6,  3),
    ],
  },
  monitor: {
    tabId: 'monitor',
    widgets: [
      w('board_pulse',        0,  0,  6,  5),
      w('board_health',       6,  0,  6,  5),
      w('alerts',             0,  5,  6,  4),
      w('analytics_snapshot', 6,  5,  6,  4),
    ],
  },
  calendar: {
    tabId: 'calendar',
    widgets: [
      w('google_calendar',  0,  0,  9,  6),
      w('my_tasks',       9,  0,  3,  6, { filter: 'due_today', maxItems: 8 }),
      w('meetings',       0,  6,  6,  3),
      w('quick_actions',  6,  6,  6,  3),
    ],
  },
  analytics: {
    tabId: 'analytics',
    widgets: [
      w('analytics_snapshot', 0, 0, 8,  6),
      w('board_health',       8, 0, 4,  3),
      w('board_pulse',        8, 3, 4,  3),
    ],
  },
  settings: {
    tabId: 'settings',
    widgets: [],
  },
};

// -------------------------------------------------------
// Preset: Minimal
// One primary widget per tab. Clean and distraction-free.
// -------------------------------------------------------

const minimalPreset: Record<string, TabLayout> = {
  home:      { tabId: 'home',      widgets: [
    w('focus_suggestion', 0, 0, 12, 3),
    w('my_tasks',         0, 3, 12, 5),
  ] },
  my_work:   { tabId: 'my_work',   widgets: [w('my_tasks',           0, 0, 12, 8)] },
  inbox:     { tabId: 'inbox',     widgets: [w('inbox',              0, 0, 12, 8)] },
  vault:     { tabId: 'vault',     widgets: [w('vault_capture',      0, 0, 6, 8), w('recent_notes', 6, 0, 6, 8)] },
  monitor:   { tabId: 'monitor',   widgets: [w('board_pulse',        0, 0, 12, 8)] },
  calendar:  { tabId: 'calendar',  widgets: [w('google_calendar',    0, 0, 12, 8)] },
  analytics: { tabId: 'analytics', widgets: [w('analytics_snapshot', 0, 0, 12, 8)] },
  settings:  { tabId: 'settings',  widgets: [] },
};

// -------------------------------------------------------
// Preset: Focused
// Combines tasks + capture + calendar for a GTD-style view.
// -------------------------------------------------------

const focusedPreset: Record<string, TabLayout> = {
  home: {
    tabId: 'home',
    widgets: [
      w('focus_suggestion', 0,  0,  8,  3),
      w('alerts',           8,  0,  4,  3),
      w('my_tasks',         0,  3,  7,  5),
      w('vault_capture',    7,  3,  5,  3),
      w('google_calendar',  7,  6,  5,  3),
    ],
  },
  my_work: {
    tabId: 'my_work',
    widgets: [
      w('my_tasks',       0,  0,  8,  8),
      w('my_tasks',       8,  0,  4,  4, { filter: 'due_today', maxItems: 5 }),
      w('google_calendar',8,  4,  4,  4),
    ],
  },
  inbox:     { tabId: 'inbox',     widgets: [w('inbox', 0, 0, 8, 8), w('alerts', 8, 0, 4, 8)] },
  vault: {
    tabId: 'vault',
    widgets: [
      w('vault_capture',  0,  0,  6,  5),
      w('recent_notes',   6,  0,  6,  5),
      w('topics',         0,  5,  6,  3),
      w('meetings',       6,  5,  6,  3),
    ],
  },
  monitor: {
    tabId: 'monitor',
    widgets: [
      w('board_health',   0,  0,  12, 5),
      w('alerts',         0,  5,  12, 3),
    ],
  },
  calendar: {
    tabId: 'calendar',
    widgets: [
      w('google_calendar',  0,  0,  8,  7),
      w('my_tasks',       8,  0,  4,  7, { filter: 'due_today', maxItems: 8 }),
    ],
  },
  analytics: { tabId: 'analytics', widgets: [w('analytics_snapshot', 0, 0, 12, 8)] },
  settings:  { tabId: 'settings',  widgets: [] },
};

// -------------------------------------------------------
// Preset registry
// -------------------------------------------------------

export const PRESETS: Record<PresetName, Record<string, TabLayout>> = {
  default:    defaultPreset,
  power_user: powerUserPreset,
  minimal:    minimalPreset,
  focused:    focusedPreset,
};

/**
 * Get the layout for a specific tab from a preset.
 * Returns the default preset tab layout if the preset doesn't define this tab.
 */
export function getPresetTabLayout(preset: PresetName, tabId: string): TabLayout {
  return (
    PRESETS[preset]?.[tabId] ??
    PRESETS['default']?.[tabId] ?? { tabId, widgets: [] }
  );
}

/**
 * Get all tab layouts for a preset (one per APP_TAB).
 */
export function getFullPreset(preset: PresetName): Record<string, TabLayout> {
  const full: Record<string, TabLayout> = {};
  APP_TABS.forEach((tab) => {
    full[tab.id] = getPresetTabLayout(preset, tab.id);
  });
  return full;
}
